<template>
	<div>
		<div class="xs-12 flex xs-center xs-middle p2" v-if="isLoading">
			<j-spinner />
		</div>
		<div v-else class="bc7 b1 r2">
			<div
				v-for="(topic, i) in topics"
				:key="i"
				class="xs-12 p2 flex xs-between xs-middle"
				:class="{ 'bb1 bc7': i < topics.length - 1 }"
			>
				<div class="fs3 fw7">{{ topic.topicName }}</div>
				<j-button
					:type="topic.isSubscribed ? 'primary' : 'bordered'"
					size="xsmall"
					@click="toggleSubscribe(topic)"
					:isLoading="isLoading"
					>{{
						topic.isSubscribed ? $t("subscribed") : $t("not_subscribed")
					}}</j-button
				>
			</div>
			<div v-if="!topics.length" class="fs3 flex xs-center xs-middle p2">
				{{ $t("errors.no_data") }}
			</div>
		</div>
		<!-- page -->
		<div class="flex xs-12 xs-center xs-middle c6 p2">
			<div class="cu2 mr2 fw7" v-if="page > 1" @click="previousPage">
				&lt;
			</div>
			<div class="fs3 fw7">{{ page }}</div>
			<div class="cu2 ml2 fw7" v-if="page < numberOfPages" @click="nextPage">
				&gt;
			</div>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import JSpinner from "@/components/JSpinner"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: { JButton, JSpinner },

	data() {
		return {
			isLoading: false,
			userId: null,

			// all notificationSubscriptions, to get the list of topicName
			listing: {
				list: [],
				numberOfItems: 0
			},
			itemsPerPage: 15,
			page: 1,

			// notificationSubscriptions where 'me.associatedUser' is subscribed
			subscribedToList: []
		}
	},

	computed: {
		topics() {
			if (!this.listing?.list?.length) return []
			return this.listing.list.map(item => {
				return {
					topicName: item.topicName,
					isSubscribed: this.subscribedToList?.includes(item.topicName)
				}
			})
		},
		numberOfPages() {
			return this.listing.numberOfItems / this.itemsPerPage
		}
	},

	async created() {
		await this.fetchUserId()
		this.fetchTopicList()
		this.fetchUserSubscriptions()
	},

	methods: {
		previousPage() {
			this.page--
			this.fetchTopicList()
		},

		nextPage() {
			this.page++
			this.fetchTopicList()
		},

		async toggleSubscribe({ topicName, isSubscribed }) {
			const variables = {
				topicName,
				entityId: this.userId
			}

			if (isSubscribed) {
				// Unsubscribe
				const query = gql`
					mutation unsubscribe($topicName: String!, $entityId: String!) {
						removeEntityFromNotificationSubscription(
							topicName: $topicName
							entityId: $entityId
						) {
							errors
							status
						}
					}
				`
				const res = await api({ query, variables })
				if (
					!(
						res?.data?.removeEntityFromNotificationSubscription?.status ===
						"success"
					)
				) {
					this.$store.dispatch("showError", [this.$t("errors.general")])
				}
			} else {
				// Subscribe
				const query = gql`
					mutation subscribe($topicName: String!, $entityId: String!) {
						addEntityToNotificationSubscription(
							topicName: $topicName
							entityId: $entityId
						) {
							errors
							status
						}
					}
				`
				const res = await api({ query, variables })
				if (
					!(
						res?.data?.addEntityToNotificationSubscription?.status === "success"
					)
				) {
					this.$store.dispatch("showError", [this.$t("errors.general")])
				}
			}
			this.fetchUserSubscriptions()
		},

		async fetchUserId() {
			const query = gql`
				query me {
					me {
						associatedUser {
							id
						}
					}
				}
			`
			this.isLoading = true
			const res = await api({ query })
			this.isLoading = false
			this.userId = res?.data?.me?.associatedUser?.id
		},

		async fetchTopicList() {
			const query = gql`
				query topicNames(
					$where: notificationSubscriptionWhereInput
					$limit: Int
					$skip: Int
					$sort: sortInput
				) {
					notificationSubscriptions(
						limit: $limit
						skip: $skip
						sort: $sort
						where: $where
					) {
						list {
							topicName
						}
					}
				}
			`

			// Sorting - sort by createdAt
			const sort = { fieldName: "topicName", type: "asc" }

			// Pagination
			const skip = (this.page - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					skip,
					limit,
					sort
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors && res?.errors?.length)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.listing.list = res?.data?.notificationSubscriptions?.list || []
			this.listing.numberOfItems =
				res?.data?.notificationSubscriptions?.totalCount || 0
		},

		async fetchUserSubscriptions() {
			if (!this.userId) {
				this.subscribedToList = []
				return
			}
			const query = gql`
				query subscriptions($where: notificationSubscriptionWhereInput) {
					notificationSubscriptions(where: $where) {
						list {
							topicName
						}
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					where: { entitiesIds_contains: this.userId }
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors && res?.errors?.length)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.subscribedToList =
				res?.data?.notificationSubscriptions?.list?.map(s => s?.topicName) || []
		}
	}
}
</script>
